import {useEffect, useRef, useState} from "react";
import useTheme from "@mui/material/styles/useTheme";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import {
    FOOTER_HEIGHT,
    HEADER_HEIGHT,
    SIDEBAR_PANEL_DRAWER_WIDTH,
    TOC_WIDTH,
} from "base/config/measurements";
import {useBaseLayer, useMap} from "base/component/geo";

import {useMainAppConfig} from "../../main/provider/MainAppProvider";
import {MainMapActions} from "./MainMapActions";
import {useMainMapProvider} from "../../main/map/MainMapProvider";

import {booleanPointInPolygon} from "@turf/boolean-point-in-polygon";
import {getGeom} from "@turf/invariant";
import {fromUrl} from "geotiff";

// x = lng, y = lat
const calculatePixelCoords = (xyCoords, image) => {
    const {x, y} = xyCoords;
    const [originX, originY] = image.getOrigin();
    const [xRes, yRes] = image.getResolution();

    const pixelX = Math.floor((x - originX) / xRes);
    const pixelY = Math.floor((y - originY) / yRes);

    return [pixelX, pixelY];
};

const getPixelValue = async (image, [pixelX, pixelY]) => {
    const [value] = await image.readRasters({
        interleave: true,
        window: [pixelX, pixelY, pixelX + 1, pixelY + 1],
        samples: [0],
    });
    return value;
};

const loadGeoTIFFImage = async url => {
    const tiff = await fromUrl(url);
    return tiff.getImage();
};

const rasters = {
    "/images/Fishinggva.png": {image: null, title: "Fishing GVA"},
    "/images/Fishingemployment.png": {image: null, title: "Fishing Employment"},
    "/images/Maritimetransportgva.png": {image: null, title: "Maritime transport GVA"},
    "/images/Maritimetransportemployment.png": {
        image: null,
        title: "Maritime transport Employment",
    },
};

const handleMapClick = async (event, _map) => {
    const clickedLatLng = event.latlng;
    const matchingFeatures = [];
    const matchingRasters = [];
    let popupContent = "";
    _map.eachLayer(function (layer) {
        if (layer.feature && layer.getTooltip()) {
            if (
                booleanPointInPolygon(
                    [clickedLatLng.lng, clickedLatLng.lat],
                    getGeom(layer.feature)
                )
            ) {
                matchingFeatures.push(layer);
            }
        }

        if (
            layer._url &&
            Object.keys(rasters).includes(layer._url) &&
            layer._bounds.contains(clickedLatLng)
        ) {
            console.log(layer);
            matchingRasters.push(rasters[layer._url]);
        }
    });

    if (matchingFeatures.length > 0 || matchingRasters.length > 0) {
        const formatLatLng = [
            clickedLatLng.lng.toFixed(3),
            clickedLatLng.lat.toFixed(3),
        ];
        popupContent += `<b>Found ${matchingFeatures.length + matchingRasters.length} elements in lng: ${formatLatLng[0]}, lat ${formatLatLng[1]}:</b><hr>`;
    }

    if (matchingFeatures.length > 0) {
        matchingFeatures.forEach(function (layer) {
            popupContent += layer.getTooltip()._content;
        });
    }

    if (matchingRasters.length > 0) {
        for (const raster of matchingRasters) {
            const pixelCoords = calculatePixelCoords(
                {x: clickedLatLng.lng, y: clickedLatLng.lat},
                raster.image
            );
            const pixelValue = await getPixelValue(raster.image, pixelCoords);
            popupContent += `
    <b>${raster.title}</b>
    <p>${pixelValue.toFixed(4)}</p>
`;
        }
    }
    if (popupContent.length > 0) {
        const popupOptions = {
            maxWidth: 625,
            minWidth: 575,
            maxHeight: 450, // If set, creates a scrollable container of the given height inside a popup if its content exceeds it. The scrollable container can be styled using the leaflet-popup-scrolled CSS class selector.
            autoPan: true, // Set it to false if you don't want the map to do panning animation to fit the opened popup.
            closeButton: true, // Controls the presence of a close button in the popup.
            autoClose: true, // Set it to false if you want to override the default behavior of the popup closing when another popup is opened.
            closeOnClick: true, // Set it if you want to override the default behavior of the popup closing when user clicks on the map. Defaults to the map's closePopupOnClick option.
            className: "",
        };
        _map.openPopup(popupContent, clickedLatLng, popupOptions);
    }
};

const MainMap = ({legend, showMapActions = false, isSidebarPanelOpen = false}) => {
    const theme = useTheme();

    const {getAllLayerProviders, getIncludedLayerProviders} = useMainMapProvider();
    const {selectedBaseLayer} = useMainAppConfig();
    const {setBaseLayer} = useBaseLayer();
    const {mapRef, createMap} = useMap();
    const mapObjectRef = useRef(null);

    // State for TOC collapse functionality
    const [isTocCollapsed, setIsTocCollapsed] = useState(false);
    const [map, setMap] = useState(null);

    const mapStyle = {
        width: isTocCollapsed ? "100%" : `calc(100% - ${TOC_WIDTH}px)`,
        height: "100%",
    };

    // Debug: Log when isSidebarPanelOpen prop changes
    // useEffect(() => {
    //     console.log("MainMap: isSidebarPanelOpen prop changed to:", isSidebarPanelOpen);
    // }, [isSidebarPanelOpen]);

    useEffect(() => {
        const _map = createMap();
        setMap(_map);
        getIncludedLayerProviders().forEach(layerProvider =>
            layerProvider.layer ? layerProvider.layer.createLayer(_map) : null
        );
        mapObjectRef.current = _map;

        _map.on("click", async event => handleMapClick(event, _map));

        async function loadRasters() {
            rasters["/images/Fishinggva.png"].image = await loadGeoTIFFImage(
                "/static/raster/Fishing_GVA_solapamientos.cog.tif"
            );
            rasters["/images/Fishingemployment.png"].image = await loadGeoTIFFImage(
                "/static/raster/Fishing_Employment_solapamientos.cog.tif"
            );
            rasters["/images/Maritimetransportgva.png"].image = await loadGeoTIFFImage(
                "/static/raster/Maritime_transport_GVA.cog.tif"
            );
            rasters["/images/Maritimetransportemployment.png"].image =
                await loadGeoTIFFImage(
                    "/static/raster/Maritime_transport_Employment.cog.tif"
                );
        }
        loadRasters();

        return () => {
            // @ts-ignore
            if (_map._loaded) {
                _map.remove();
            }
        };
    }, []);

    useEffect(() => {
        if (mapObjectRef.current) {
            setBaseLayer(mapObjectRef.current, selectedBaseLayer);
        }
    }, [selectedBaseLayer]);

    getAllLayerProviders().forEach(layerProvider => layerProvider.useProvider());

    const handleToggleToc = () => {
        setIsTocCollapsed(!isTocCollapsed);
    };

    // Calculate the right offset for the toggle button based on sidebar states
    // const calculateRightOffset = () => {
    //     if (isTocCollapsed) {
    //         return isSidebarPanelOpen ? `${SIDEBAR_PANEL_DRAWER_WIDTH}px` : "0px";
    //     } else {
    //         return isSidebarPanelOpen
    //             ? `${TOC_WIDTH + SIDEBAR_PANEL_DRAWER_WIDTH}px`
    //             : `${TOC_WIDTH}px`;
    //     }
    // };

    // Calculate the right offset for the toggle button based on sidebar states
    const calculateRightOffset = () => {
        if (isTocCollapsed) {
            return "0px";
        } else {
            return `${TOC_WIDTH + 55}px`;
        }
    };

    return (
        <Stack
            direction="row"
            sx={{height: `calc(100vh - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`}}
        >
            <div id="map" style={mapStyle} ref={mapRef} />

            {/* Toggle button for TOC */}
            <Tooltip title={isTocCollapsed ? "Expand" : "Collapse"}>
                <IconButton
                    onClick={handleToggleToc}
                    sx={{
                        position: "absolute",
                        top: `${HEADER_HEIGHT}px`,
                        right: calculateRightOffset(),
                        zIndex: 9000,
                        borderRadius: isTocCollapsed ? "8px" : "8px",
                        backgroundColor: "transparent",
                        border: 0,
                        boxShadow: 0,
                        "&:hover": {
                            backgroundColor: theme.palette.primary.lighter,
                        },
                        mt: 0.5,
                        mr: 0.5,
                    }}
                >
                    {isTocCollapsed ? (
                        <KeyboardDoubleArrowLeftIcon />
                    ) : (
                        <KeyboardDoubleArrowRightIcon />
                    )}
                </IconButton>
            </Tooltip>

            {/* Collapsible TOC Section */}
            <Collapse
                in={!isTocCollapsed}
                orientation="horizontal"
                onExited={() => {
                    map.invalidateSize(true);
                }}
            >
                <Stack sx={{height: "100%"}}>
                    <MainMapActions
                        mapObjectRef={mapObjectRef}
                        mapRef={mapRef}
                        showMapActions={showMapActions}
                    />
                    {legend}
                </Stack>
            </Collapse>
        </Stack>
    );
};

export default MainMap;
